/* BP-019 — Executive Dashboard.
   Cross-module KPI command center. Session 1: deterministic briefing.
   Session 2 will layer AI briefing + PDF export on top. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, Minus,
  DollarSign, Wallet, FileText, Handshake, Percent,
  Activity, Target, AlertTriangle, Sparkles, Info,
  Zap, Layers, Download, Brain, Shield, ChevronDown, ChevronUp,
} from "lucide-react";


const KPI_ICON = {
  revenue:                DollarSign,
  pipeline_value:         Layers,
  contracts:              FileText,
  assignments:            Handshake,
  net_profit:             Wallet,
  cash_flow:              Activity,
  marketing_roi:          Percent,
  avg_assignment_fee:     Target,
  close_rate:             TrendingUp,
  lead_response_time_min: Zap,
};


const WINDOW_OPTIONS = [
  { value: 7,   label: "7d"  },
  { value: 30,  label: "30d" },
  { value: 90,  label: "90d" },
  { value: 180, label: "6mo" },
  { value: 365, label: "1yr" },
];


function fmt(value, fmtType) {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(n)) return "—";
  if (fmtType === "currency") {
    if (Math.abs(n) >= 1_000_000)
      return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 10_000)
      return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toLocaleString(undefined, {
      maximumFractionDigits: 0 })}`;
  }
  if (fmtType === "percent") return `${n.toFixed(1)}%`;
  if (fmtType === "duration_min") {
    if (n < 60) return `${Math.round(n)}m`;
    if (n < 60 * 24) return `${(n / 60).toFixed(1)}h`;
    return `${(n / 60 / 24).toFixed(1)}d`;
  }
  return n.toLocaleString();
}



const DIMENSION_ICON = {
  financial:   DollarSign,
  pipeline:    Layers,
  marketing:   Percent,
  acquisition: Target,
  disposition: Handshake,
  operational: Zap,
};


function HealthScoreHero({ health }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!health) return null;

  const {
    score, band, delta, prior_score, coverage_pct, dimensions,
    formula_version,
  } = health;

  const trending = delta === null || delta === undefined || delta === 0
    ? "flat"
    : delta > 0 ? "up" : "down";
  const TrendIcon = trending === "up" ? TrendingUp
                  : trending === "down" ? TrendingDown : Minus;

  return (
    <div
      data-testid="exec-health-hero"
      className="relative overflow-hidden rounded-2xl border-2 p-6 shadow-lg
                 transition-all duration-300"
      style={{
        background: `linear-gradient(135deg, ${band?.bg || "#f3f4f6"} 0%, #ffffff 65%)`,
        borderColor: band?.text ? `${band.text}33` : "#e5e7eb",
      }}
    >
      {/* Ambient ring */}
      <div
        className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-20"
        style={{ background: band?.text || "#f59e0b" }}
        aria-hidden="true"
      />

      <div className="relative flex flex-wrap items-start justify-between gap-6">
        {/* ── Left: score + band + trend */}
        <div className="flex-1 min-w-0">
          <div
            className="flex items-center gap-2 text-[11px] uppercase
                       tracking-widest font-semibold"
            style={{ color: band?.text || "#78350f" }}
          >
            <Shield size={13} />
            Executive Health Score
          </div>

          <div className="mt-2 flex items-baseline gap-3 flex-wrap">
            <div
              data-testid="exec-health-score-value"
              className="text-6xl md:text-7xl font-bold tabular-nums leading-none"
              style={{ color: band?.text || "#1f2937" }}
            >
              {score}
            </div>
            <div className="text-2xl text-slate-400 font-medium">/100</div>

            <div
              data-testid="exec-health-band"
              className="inline-flex items-center px-3 py-1 rounded-full
                         text-xs font-bold uppercase tracking-wider ring-2"
              style={{
                color: band?.text || "#1f2937",
                background: band?.bg || "#f3f4f6",
                borderColor: band?.text || "#e5e7eb",
              }}
            >
              {band?.label || "—"}
            </div>
          </div>

          {/* Trend + prior + coverage */}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <span
              data-testid="exec-health-trend"
              data-trending={trending}
              className={`inline-flex items-center gap-1 font-semibold
                          ${trending === "up"   ? "text-emerald-700"
                          : trending === "down" ? "text-red-700"
                          :                       "text-slate-500"}`}
            >
              <TrendIcon size={14} />
              {delta === null || delta === undefined
                ? "no baseline"
                : `${delta > 0 ? "+" : ""}${delta} vs prior`}
            </span>

            {prior_score !== null && prior_score !== undefined && (
              <span className="text-slate-500 text-xs">
                Prior: {prior_score}
              </span>
            )}

            <span
              data-testid="exec-health-coverage"
              className="text-xs px-2 py-0.5 rounded-full bg-white/70
                         ring-1 ring-slate-200 text-slate-600"
              title="Percent of dimension weight backed by real data"
            >
              Evidence coverage: {coverage_pct}%
            </span>

            <span className="text-[10px] text-slate-400 font-mono
                              ml-auto md:ml-0">
              {formula_version}
            </span>
          </div>
        </div>

        {/* ── Right: mini dimension summary */}
        <div className="min-w-[260px] max-w-[420px] w-full md:w-auto">
          <div className="text-[11px] uppercase tracking-wider text-slate-500
                          font-semibold mb-2 flex items-center justify-between">
            <span>Dimensions</span>
            <button
              onClick={() => setExpanded((e) => !e)}
              data-testid="exec-health-expand"
              className="inline-flex items-center gap-1 text-slate-500
                         hover:text-slate-900 normal-case font-normal
                         text-[11px] tracking-normal"
            >
              {expanded ? "Collapse" : "Explain"}
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          </div>
          <div className="space-y-1.5">
            {dimensions?.map((dim) => (
              <DimensionRow key={dim.id} dim={dim} expanded={expanded} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function DimensionRow({ dim, expanded }) {
  const Icon = DIMENSION_ICON[dim.id] || Activity;
  const scoreColor = dim.data_gap        ? "#94a3b8"
                    : dim.score >= 75    ? "#047857"
                    : dim.score >= 40    ? "#b45309"
                    :                       "#b91c1c";
  return (
    <div
      data-testid={`exec-health-dim-${dim.id}`}
      className="rounded-lg bg-white/70 ring-1 ring-slate-200/70 px-3 py-1.5"
    >
      <div className="flex items-center gap-2">
        <div
          className="h-6 w-6 rounded grid place-items-center"
          style={{ background: `${scoreColor}22`, color: scoreColor }}
        >
          <Icon size={12} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium text-slate-700 truncate">
            {dim.label}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div
            className="text-sm font-bold tabular-nums"
            style={{ color: scoreColor }}
          >
            {Math.round(dim.score)}
          </div>
          <div className="text-[9px] text-slate-400 -mt-0.5">
            w{dim.weight}
          </div>
        </div>
      </div>
      {expanded && (
        <div
          data-testid={`exec-health-dim-${dim.id}-detail`}
          className="mt-1.5 pl-8 text-[10px] text-slate-600 leading-snug"
        >
          <div>{dim.rationale}</div>
          <div className="text-slate-400 italic mt-0.5">
            Target: {dim.target}
          </div>
          {dim.data_gap && (
            <div className="text-amber-700 font-medium mt-0.5">
              ⚠ Data gap — dimension excluded from evidence coverage.
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function DeltaBadge({ value }) {
  if (value === null || value === undefined) {
    return (
      <span
        data-testid="exec-delta-none"
        className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"
      >
        <Minus size={10} /> baseline
      </span>
    );
  }
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive
    ? "text-emerald-600 bg-emerald-50 ring-emerald-200"
    : "text-red-600   bg-red-50   ring-red-200";
  return (
    <span
      data-testid="exec-delta-value"
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                  text-[10px] font-medium ring-1 ${cls}`}
    >
      <Icon size={10} />
      {positive ? "+" : ""}{value.toFixed(1)}%
    </span>
  );
}


function KpiTile({ id, kpi, meta, delta }) {
  const Icon = KPI_ICON[id] || Activity;
  return (
    <div
      data-testid={`exec-kpi-${id}`}
      className="rounded-xl border border-slate-200 bg-white p-4
                 hover:border-amber-300 hover:shadow-sm transition-all
                 group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-amber-50 text-amber-600
                          grid place-items-center ring-1 ring-amber-100">
            <Icon size={15} />
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            {meta.label}
          </div>
        </div>
        <div
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          title={meta.explain}
        >
          <Info size={12} className="text-slate-400" />
        </div>
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <div className="text-2xl font-semibold text-slate-900 tabular-nums">
          {fmt(kpi, meta.format)}
        </div>
        <DeltaBadge value={delta} />
      </div>
      <div className="mt-2 text-[11px] text-slate-500 line-clamp-2">
        {meta.explain}
      </div>
    </div>
  );
}


function BriefingCard({ briefing, loading, onRefresh,
                          onGenerateAI, generatingAI }) {
  const isAI = briefing?.mode === "ai";
  const modeClasses = isAI
    ? "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 ring-1 ring-indigo-200"
    : "bg-slate-100 text-slate-500";
  return (
    <div
      data-testid="exec-briefing-card"
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-amber-700">
          {isAI
            ? <Brain size={16} className="text-indigo-600" />
            : <Sparkles size={16} />}
          <div className="text-sm font-semibold">
            {isAI ? "AI Executive Briefing" : "Executive Briefing"}
          </div>
          <span
            data-testid="exec-briefing-mode"
            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5
                        rounded font-medium ${modeClasses}`}
          >
            {briefing?.mode || "loading"}
          </span>
          {isAI && typeof briefing?.confidence === "number" && (
            <span
              data-testid="exec-briefing-confidence"
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5
                         rounded bg-indigo-50 text-indigo-700 ring-1
                         ring-indigo-100"
            >
              conf {(briefing.confidence * 100).toFixed(0)}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onGenerateAI}
            disabled={generatingAI}
            data-testid="exec-briefing-generate-ai"
            className="text-xs px-3 py-1 rounded-md
                       bg-gradient-to-r from-indigo-500 to-purple-500
                       text-white hover:from-indigo-600 hover:to-purple-600
                       flex items-center gap-1 shadow-sm
                       disabled:opacity-50"
          >
            {generatingAI
              ? <Loader2 size={12} className="animate-spin" />
              : <Brain size={12} />}
            {isAI ? "Regenerate AI" : "Generate AI Briefing"}
          </button>
          <button
            onClick={onRefresh}
            disabled={loading}
            data-testid="exec-briefing-refresh"
            className="text-xs px-2 py-1 rounded-md border border-slate-200
                       hover:bg-slate-50 flex items-center gap-1
                       disabled:opacity-50"
          >
            {loading
              ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
      </div>

      <div
        data-testid="exec-briefing-headline"
        className="mt-3 text-lg font-medium text-slate-900"
      >
        {briefing?.headline || "—"}
      </div>

      {briefing?.fallback_reason && (
        <div
          data-testid="exec-briefing-fallback-notice"
          className="mt-2 text-[11px] rounded-md bg-amber-50 text-amber-800
                     ring-1 ring-amber-100 px-2 py-1 inline-block"
        >
          AI unavailable — showing deterministic briefing
          <span className="text-amber-500 ml-1">
            ({briefing.fallback_reason})
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-4">
        <BriefingList
          testid="exec-briefing-strengths"
          label="Strengths"
          items={briefing?.strengths}
          Icon={TrendingUp}
          tone="emerald"
        />
        <BriefingList
          testid="exec-briefing-risks"
          label="Risks"
          items={briefing?.risks}
          Icon={AlertTriangle}
          tone="red"
        />
        <BriefingList
          testid="exec-briefing-opportunities"
          label="Top Opportunities"
          items={briefing?.top_opportunities}
          Icon={Target}
          tone="sky"
        />
        <BriefingList
          testid="exec-briefing-actions"
          label="Recommended Actions"
          items={briefing?.recommended_actions}
          Icon={Zap}
          tone="amber"
        />
      </div>

      {briefing?.disclaimer && (
        <div className="mt-4 text-[11px] text-slate-400 italic">
          {briefing.disclaimer}
        </div>
      )}
    </div>
  );
}


function BriefingList({ testid, label, items, Icon, tone }) {
  const toneCls = {
    emerald: "text-emerald-700 bg-emerald-50 ring-emerald-100",
    red:     "text-red-700     bg-red-50     ring-red-100",
    sky:     "text-sky-700     bg-sky-50     ring-sky-100",
    amber:   "text-amber-700   bg-amber-50   ring-amber-100",
  }[tone] || "text-slate-700 bg-slate-50 ring-slate-100";
  return (
    <div data-testid={testid}>
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded
                       text-[11px] font-medium ring-1 ${toneCls}`}>
        <Icon size={11} />
        {label}
      </div>
      <ul className="mt-2 space-y-1.5 text-xs text-slate-700">
        {(items && items.length > 0)
          ? items.map((it, i) => {
              const text = (typeof it === "string")
                ? it
                : (it?.text || "");
              const evidence = (typeof it === "object" && it !== null)
                ? (it.evidence_paths || [])
                : [];
              return (
                <li key={i} className="leading-snug">
                  • {text}
                  {evidence.length > 0 && (
                    <span
                      data-testid={`${testid}-evidence-${i}`}
                      className="ml-1 text-[10px] text-slate-400 font-mono"
                    >
                      [{evidence.join(", ")}]
                    </span>
                  )}
                </li>
              );
            })
          : <li className="text-slate-400 italic">
              No items in this window.
            </li>}
      </ul>
    </div>
  );
}


function ForecastCard({ forecast, loading }) {
  return (
    <div
      data-testid="exec-forecast-card"
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sky-700">
          <TrendingUp size={16} />
          <div className="text-sm font-semibold">
            Forecast · Next {forecast?.horizon_days || 30}d
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          {forecast?.method?.replace(/_/g, " ") || "—"}
        </span>
      </div>
      {loading ? (
        <div className="mt-4 flex items-center justify-center py-6">
          <Loader2 size={16} className="animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {["revenue", "cash_flow", "net_profit",
             "assignments", "contracts"].map((k) => (
            <div
              key={k}
              data-testid={`exec-forecast-${k}`}
              className="rounded-lg bg-sky-50/50 ring-1 ring-sky-100 p-3"
            >
              <div className="text-[10px] uppercase tracking-wider text-sky-700">
                {k.replace(/_/g, " ")}
              </div>
              <div className="mt-1 text-base font-semibold text-slate-900 tabular-nums">
                {fmt(forecast?.projected?.[k],
                      ["assignments", "contracts"].includes(k)
                        ? "count" : "currency")}
              </div>
              <div className="text-[10px] text-slate-500">
                trailing: {fmt(forecast?.trailing?.[k],
                                ["assignments", "contracts"].includes(k)
                                  ? "count" : "currency")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function ExecutiveDashboardPage() {
  const [window, setWindow]     = useState(30);
  const [horizon, setHorizon]   = useState(90);
  const [dash, setDash]         = useState(null);
  const [forecast, setForecast] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [refreshingBriefing, setRefreshingBriefing] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, f] = await Promise.all([
        api.get(`/v3/executive/dashboard?window_days=${window}`),
        api.get(`/v3/executive/forecast?window_days=${window}&horizon_days=${horizon}`),
      ]);
      setDash(d.data);
      setForecast(f.data);

      // Briefing: prefer latest AI briefing if one exists for this window;
      // fall back to deterministic briefing endpoint.
      try {
        const { data } = await api.get(
          `/v3/executive/briefing/ai/latest?window_days=${window}`);
        setBriefing(data);
      } catch {
        const det = await api.get(
          `/v3/executive/briefing?window_days=${window}`);
        setBriefing(det.data);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          || "Failed to load executive dashboard");
    } finally {
      setLoading(false);
    }
  }, [window, horizon]);

  useEffect(() => { load(); }, [load]);

  const refreshBriefing = useCallback(async () => {
    setRefreshingBriefing(true);
    try {
      // Same latest-AI-first strategy.
      try {
        const { data } = await api.get(
          `/v3/executive/briefing/ai/latest?window_days=${window}`);
        setBriefing(data);
      } catch {
        const { data } = await api.get(
          `/v3/executive/briefing?window_days=${window}`);
        setBriefing(data);
      }
      toast.success("Briefing refreshed");
    } catch {
      toast.error("Failed to refresh briefing");
    } finally {
      setRefreshingBriefing(false);
    }
  }, [window]);

  const generateAIBriefing = useCallback(async () => {
    setGeneratingAI(true);
    toast.message("Generating AI briefing…", { duration: 3000 });
    try {
      const { data } = await api.post(
        "/v3/executive/briefing/ai",
        { window_days: window, force: true });
      setBriefing(data);
      if (data.mode === "ai") {
        toast.success("AI briefing generated");
      } else {
        toast.warning(
          `AI unavailable — showing deterministic briefing (${data.fallback_reason || "fallback"})`);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          || "Failed to generate AI briefing");
    } finally {
      setGeneratingAI(false);
    }
  }, [window]);

  const downloadPDF = useCallback(async () => {
    setDownloadingPdf(true);
    try {
      const resp = await api.get(
        `/v3/executive/briefing/pdf?window_days=${window}&horizon_days=${horizon}&include_ai=true`,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data],
        { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `executive-briefing-${window}d.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          || "Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  }, [window, horizon]);

  const kpiEntries = useMemo(() => {
    if (!dash) return [];
    const orderedKeys = [
      "revenue", "pipeline_value", "contracts", "assignments",
      "net_profit", "cash_flow", "marketing_roi",
      "avg_assignment_fee", "close_rate", "lead_response_time_min",
    ];
    return orderedKeys.map((k) => ({
      id:    k,
      kpi:   dash.kpis?.[k],
      meta:  dash.kpi_meta?.[k] || { label: k, format: "count", explain: "" },
      delta: dash.deltas?.[k],
    }));
  }, [dash]);

  return (
    <div className="space-y-6" data-testid="exec-dashboard-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-amber-600">
            Executive Command Center · BP-019
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Executive Dashboard
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Cross-module KPIs from Deal Score, Strategy, Marketing,
            Lead Scoring, Acquisition, and Disposition intelligence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            data-testid="exec-window-selector"
            className="inline-flex rounded-lg bg-white border border-slate-200 p-0.5"
          >
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setWindow(opt.value)}
                data-testid={`exec-window-${opt.value}`}
                className={`px-2.5 py-1 text-xs rounded-md transition
                            ${window === opt.value
                              ? "bg-amber-500 text-white shadow-sm"
                              : "text-slate-600 hover:bg-slate-50"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="exec-refresh-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200
                       bg-white hover:bg-slate-50 flex items-center gap-1
                       disabled:opacity-50"
          >
            {loading
              ? <Loader2 size={13} className="animate-spin" />
              : <RefreshCw size={13} />}
            Refresh
          </button>
          <button
            onClick={downloadPDF}
            disabled={downloadingPdf || loading}
            data-testid="exec-download-pdf-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-amber-300
                       bg-amber-50 hover:bg-amber-100 text-amber-800
                       flex items-center gap-1 disabled:opacity-50"
          >
            {downloadingPdf
              ? <Loader2 size={13} className="animate-spin" />
              : <Download size={13} />}
            PDF
          </button>
        </div>
      </div>

      {loading && !dash ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12
                         grid place-items-center">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          <HealthScoreHero health={dash?.health_score} />

          <div
            data-testid="exec-kpi-grid"
            className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
          >
            {kpiEntries.map((k) => (
              <KpiTile key={k.id} {...k} />
            ))}
          </div>

          <BriefingCard
            briefing={briefing}
            loading={refreshingBriefing}
            onRefresh={refreshBriefing}
            onGenerateAI={generateAIBriefing}
            generatingAI={generatingAI}
          />

          <ForecastCard forecast={forecast} loading={loading} />

          {dash?.modules?.integrated && (
            <div
              data-testid="exec-modules-card"
              className="rounded-xl border border-slate-200 bg-white p-4"
            >
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                Integrated Modules
              </div>
              <div className="flex flex-wrap gap-1.5">
                {dash.modules.integrated.map((m) => (
                  <span
                    key={m.id}
                    data-testid={`exec-module-${m.id}`}
                    className="inline-flex items-center gap-1 rounded-full
                               px-2 py-0.5 text-[11px] ring-1
                               ring-slate-200 bg-slate-50 text-slate-700"
                  >
                    <span className="text-slate-400 font-mono">{m.id}</span>
                    <span>{m.name}</span>
                  </span>
                ))}
              </div>
              <div className="mt-3 text-[10px] text-slate-400 tabular-nums">
                Formula {dash.formula_version} · window {dash.window_days}d
                · as of {new Date(dash.as_of).toLocaleString()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
